import React from 'react';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm, useWatch } from 'react-hook-form';
import HStack from '../../../../../../Components/Base/Containers/HStack';
import InputField from '../../../../../../Components/Form/components/InputField';
import WorkplaceSelectField from '../../../../../Base/Components/Inputs/WorkplaceSelect/WorkplaceSelectField';
import RoleMultiSelectWithCheckListField from '../../../../../Base/Components/Inputs/RoleMultiSelectWithCheckList/RoleMultiSelectWithCheckListField';
import { WorkplaceAclRole } from '../../../../../Workplace/Enums/WorkplaceAclRole.enum';
import FormMultiplierRemoveButton from '../../../../../../Components/Form/FormMultiplierRemoveButton';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../../Enums/Theme.enum';
import FormMultiplier from '../../../../../../Components/Form/FormMultiplier';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import PhoneNumberField from '../../../../../../Components/PhoneNumber/PhoneNumberField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const validationScheme = yup.object({
	salutationBefore: yup.string().notRequired().nullable(),
	salutationAfter: yup.string().notRequired().nullable(),
	firstName: yup
		.string()
		.required('validation.managerAddWorkerByRegistrationForm.firstName'),
	lastName: yup
		.string()
		.required('validation.managerAddWorkerByRegistrationForm.lastName'),
	email: yup
		.string()
		.email('validation.managerAddWorkerByRegistrationForm.email')
		.required('validation.managerAddWorkerByRegistrationForm.email'),
	phoneNumber: yup.string().notRequired().nullable(),
	workplaces: yup
		.array()
		.of(
			yup.object().shape({
				workplace: yup.object().shape({
					label: yup.string(),
					value: yup
						.string()
						.required(
							'validation.managerAddWorkerByRegistrationForm.workplace',
						),
				}),
				role: yup
					.mixed()
					.nullable()
					.required('validation.managerAddWorkerByRegistrationForm.role'),
			}),
		)
		.required(),
});

export interface ManagerAddWorkerByRegistrationFormValues {
	salutationBefore?: string;
	salutationAfter?: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	workplaces: {
		workplace: {
			value: string;
			label: string;
		};
		role: {
			value: string;
			label: string;
		}[];
	}[];
}

interface Props {
	onSubmit: (values: ManagerAddWorkerByRegistrationFormValues) => void;
}

const ManagerAddWorkerByRegistrationForm: React.FC<Props> = ({ onSubmit }) => {
	const formMethods = useForm<ManagerAddWorkerByRegistrationFormValues>({
		resolver: yupResolver(validationScheme),
	});

	const handleSubmit = (values: ManagerAddWorkerByRegistrationFormValues) => {
		onSubmit(values);
	};

	const workplacesValues =
		useWatch({
			control: formMethods.control,
			name: 'workplaces',
			defaultValue: [],
		}) ?? [];

	const selectedWorkplaceIds: string[] = workplacesValues.flatMap(
		(w: {
			workplace?: {
				value: string;
				label: string;
			};
		}) => w.workplace?.value ?? [],
	);

	return (
		<FormWrapper formMethods={formMethods}>
			<HStack>
				<InputField
					name='salutationBefore'
					label='base.shared.salutationBefore'
					isWrapped
				/>
				<InputField
					name='salutationAfter'
					label='base.shared.salutationAfter'
					isWrapped
				/>
			</HStack>
			<HStack>
				<InputField
					name='firstName'
					label='base.shared.firstName'
					isRequired
					isWrapped
				/>
				<InputField
					name='lastName'
					label='base.shared.lastName'
					isRequired
					isWrapped
				/>
			</HStack>
			<InputField
				name='email'
				label='component.workplaceAndWorkers.enterWorkersEmail'
				isRequired
				isWrapped
			/>
			<PhoneNumberField
				name='phoneNumber'
				label='component.workplaceAndWorkers.enterWorkersPhone'
				isWrapped
			/>
			<FormMultiplier
				name='workplaces'
				addButtonTitle='base.addWorkerToWorkplace'
				createDefault={1}
				min={1}>
				<HStack>
					<WorkplaceSelectField
						name='workplace'
						label='base.chooseWorkplace'
						isRequired
						isWrapped
						portal
						excludedIds={selectedWorkplaceIds}
					/>
					<RoleMultiSelectWithCheckListField
						name='role'
						label='base.chooseWorkerRole'
						isRequired
						isWrapped
						portal
						excludeRoleKeys={[WorkplaceAclRole.ADMIN]}
					/>
				</HStack>
				<div className='d-flex justify-content-end'>
					<FormMultiplierRemoveButton>
						<LinkButton title='base.shared.remove' theme={Theme.RED} />
					</FormMultiplierRemoveButton>
				</div>
			</FormMultiplier>
			<div>
				<SubmitButton onSubmit={handleSubmit} title='base.register' />
			</div>
		</FormWrapper>
	);
};

export default ManagerAddWorkerByRegistrationForm;
